// ============================================================================
// État du lead partagé entre les étapes du funnel (routes /lp → /vsl → /ty|/nok).
//   La SPA est pilotée par la route ; l'état (nom, qualifié, variante) est
//   persisté en sessionStorage pour survivre à la navigation, au refresh et à
//   l'entrée directe sur une étape. S'efface à la fermeture de l'onglet.
// ============================================================================

export type FunnelLead = {
  nomComplet: string;
  email: string;
  phone: string; // E.164
  firstName: string;
  qualified: boolean;
  variant: string; // variante A/B home-copy au moment de la soumission
};

const KEY = "funnel_lead";

export function saveLead(lead: FunnelLead): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(lead));
  } catch {
    /* stockage indisponible : le funnel continue sans persistance */
  }
}

export function loadLead(): FunnelLead | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as FunnelLead) : null;
  } catch {
    return null;
  }
}

// ============================================================================
// Funnel d'origine du lead — `vsl` | `masterclass`
//   Deux LP publicitaires coexistent : /lp → /vsl et /mc → /masterclass. Les
//   étapes partagées (/nok, /ty) ne portent pas l'info dans leur route : on la
//   persiste dès l'entrée sur une LP, pour que le webhook n8n ET PostHog
//   sachent toujours de quel funnel vient le lead.
// ============================================================================

export const FUNNEL_KINDS = ["vsl", "masterclass"] as const;
export type FunnelKind = (typeof FUNNEL_KINDS)[number];

// Funnel historique : si rien n'est stocké (entrée directe sur /ty par ex.),
// on retombe sur `vsl` plutôt que sur une valeur vide côté n8n/PostHog.
export const DEFAULT_FUNNEL_KIND: FunnelKind = "vsl";

const KIND_KEY = "funnel_kind";

export function saveFunnelKind(kind: FunnelKind): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KIND_KEY, kind);
  } catch {
    /* stockage indisponible : le funnel continue sans persistance */
  }
}

/** Toujours une valeur exploitable (jamais null) : stockée, sinon défaut. */
export function loadFunnelKind(): FunnelKind {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_KIND;
  try {
    const raw = window.sessionStorage.getItem(KIND_KEY);
    return FUNNEL_KINDS.includes(raw as FunnelKind)
      ? (raw as FunnelKind)
      : DEFAULT_FUNNEL_KIND;
  } catch {
    return DEFAULT_FUNNEL_KIND;
  }
}
